import { displayTimelineProvenance, type ContextEvent } from "../athleteContext";
import type { PreferredUnits } from "../../athlete-profile/types/athleteProfile";
import { formatWeightFromKilograms } from "../../../lib/unitPreferences";

type ContextTimelineProps = {
  events: ContextEvent[];
  emptyMessage?: string;
  onOpenRelatedActivity?: (activityId: string) => void;
  onOpenDailyHealthDate?: (localDate: string) => void;
  preferredUnits?: PreferredUnits;
};

function formatDateLabel(dateKey: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return dateKey;

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, monthIndex, day, 12, 0, 0);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatEventType(type: ContextEvent["type"]): string {
  return type.replaceAll("_", " ");
}

function bodyWeightMetricLabel(metrics: Record<string, number> | undefined): string | null {
  if (!metrics) return null;
  return null;
}

function bodyWeightMetricLabelByUnits(metrics: Record<string, number> | undefined, preferredUnits: PreferredUnits): string | null {
  if (!metrics) return null;
  if (typeof metrics.weight_kg === "number") return formatWeightFromKilograms(metrics.weight_kg, preferredUnits);
  if (typeof metrics.weight_lb === "number") return formatWeightFromKilograms(metrics.weight_lb * 0.45359237, preferredUnits);
  return null;
}

function extractRelatedActivityId(sourceId: string | undefined): string | null {
  if (!sourceId) return null;
  return sourceId
    .replace(/^race:/, "")
    .replace(/^ride_analysis:/, "")
    .replace(/^ride:/, "")
    .replace(/^ftp_snapshot:/, "")
    .replace(/^activity_completed:/, "")
    .replace(":race", "")
    .trim() || null;
}

export function ContextTimeline({ events, emptyMessage = "No related context found.", onOpenRelatedActivity, onOpenDailyHealthDate, preferredUnits = "imperial" }: ContextTimelineProps) {
  const groups = new Map<string, ContextEvent[]>();
  for (const event of events) {
    const bucket = groups.get(event.eventDate) ?? [];
    bucket.push(event);
    groups.set(event.eventDate, bucket);
  }

  const groupedEvents = [...groups.entries()].sort((left, right) => Date.parse(right[0]) - Date.parse(left[0]));

  if (groupedEvents.length === 0) {
    return <p className="command-note">{emptyMessage}</p>;
  }

  return (
    <div className="timeline-event-groups">
      {groupedEvents.map(([date, dayEvents]) => (
        <section key={date} className="timeline-day-group">
          <h3>{formatDateLabel(date)}</h3>
          <ul className="timeline-event-list">
            {dayEvents.map(event => {
              const activityId = event.source === "automatic_completed_activity"
                ? extractRelatedActivityId(event.sourceId)
                : null;
              return (
                <li key={event.id} className="timeline-event-item">
                  <div>
                    <p className="timeline-event-type">{formatEventType(event.type)}</p>
                    <strong>{event.title}</strong>
                    <p className="command-note">{event.summary}</p>
                    {event.type === "body_weight" ? <p className="command-note">Body weight: {bodyWeightMetricLabelByUnits(event.metrics, preferredUnits) ?? "Not provided"}</p> : null}
                    <p className="command-note">
                      Source: {displayTimelineProvenance(event.source)}
                      {event.confidence ? ` · Confidence: ${event.confidence}` : ""}
                    </p>
                  </div>
                  <div className="timeline-event-actions">
                    {activityId && onOpenRelatedActivity ? (
                      <button type="button" className="ride-analysis-button secondary-action" onClick={() => onOpenRelatedActivity(activityId)}>
                        Open Related Activity
                      </button>
                    ) : null}
                    {event.source === "imported_source" && event.tags.includes("daily_health") && onOpenDailyHealthDate ? (
                      <button type="button" className="ride-analysis-button secondary-action" onClick={() => onOpenDailyHealthDate(event.eventDate)}>
                        Open Daily Health
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}